import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  ShieldCheck,
  LogOut,
  Loader2,
  Sparkles,
  TrendingUp,
  Clock,
  BadgeCheck,
  ShieldAlert,
  FileWarning,
  Ban,
  CalendarClock,
  ChevronRight,
  ChevronDown,
  Wallet,
  ExternalLink,
  Copy,
  Radar,
  CreditCard,
} from "lucide-react";
import { dashboardCases, dashboardSummary, api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import ProgressDashboard from "@/components/dashboard/ProgressDashboard";

const STATUS_META = {
  submitted: { label: "Submitted", icon: FileWarning, color: "#94A3B8", bg: "bg-white/[0.04]", border: "border-white/10" },
  under_review: { label: "Under review", icon: Radar, color: "#94A3B8", bg: "bg-white/[0.04]", border: "border-white/10" },
  filed: { label: "Filed with Google", icon: FileWarning, color: "#D4AF37", bg: "bg-[#D4AF37]/[0.08]", border: "border-[#D4AF37]/30" },
  escalated: { label: "Escalated", icon: ShieldAlert, color: "#D4AF37", bg: "bg-[#D4AF37]/[0.10]", border: "border-[#D4AF37]/40" },
  removed: { label: "Removed", icon: BadgeCheck, color: "#10B981", bg: "bg-[#10B981]/[0.10]", border: "border-[#10B981]/40" },
  not_eligible: { label: "Not eligible", icon: Ban, color: "#94A3B8", bg: "bg-white/[0.02]", border: "border-white/10" },
};

const STAGE_ORDER = ["submitted", "filed", "escalated", "removed"];

function stageIndex(status) {
  if (status === "not_eligible") return -1;
  if (status === "under_review") return 0;
  return STAGE_ORDER.indexOf(status);
}

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (days <= 0) return "today";
  if (days === 1) return "1 day ago";
  if (days < 30) return `${days} days ago`;
  const months = Math.floor(days / 30);
  if (months === 1) return "1 month ago";
  return `${months} months ago`;
}

export default function DashboardPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [summary, setSummary] = useState(null);
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [payingCaseId, setPayingCaseId] = useState(null);

  const reload = async () => {
    try {
      const [s, c] = await Promise.all([dashboardSummary(), dashboardCases()]);
      setSummary(s);
      setCases(c);
    } catch (e) {
      toast.error("Could not load your dashboard.");
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await reload();
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle post-checkout redirect: poll status, then refresh dashboard
  useEffect(() => {
    const checkout = searchParams.get("checkout");
    const sessionId = searchParams.get("session_id");
    if (!checkout) return;

    if (checkout === "canceled") {
      toast.info("Checkout canceled — no charge made.");
      setSearchParams({}, { replace: true });
      return;
    }
    if (checkout === "success" && sessionId) {
      let attempts = 0;
      const maxAttempts = 8;
      let cancelled = false;
      toast.info("Confirming your payment…");

      const poll = async () => {
        if (cancelled) return;
        try {
          const { data } = await api.get(`/payments/status/${sessionId}`);
          if (data.payment_status === "paid") {
            toast.success("Payment received — thank you!");
            await reload();
            setSearchParams({}, { replace: true });
            return;
          }
          if (data.status === "expired") {
            toast.error("Checkout session expired.");
            setSearchParams({}, { replace: true });
            return;
          }
        } catch (e) {
          // fallthrough to retry
        }
        attempts += 1;
        if (attempts < maxAttempts) {
          setTimeout(poll, 1800);
        } else {
          toast.info("Payment is still processing. It'll show as paid shortly.");
          setSearchParams({}, { replace: true });
        }
      };
      poll();
      return () => {
        cancelled = true;
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const payInvoice = async (caseId) => {
    setPayingCaseId(caseId);
    try {
      const { data } = await api.post("/payments/checkout", {
        package_id: "review_removal",
        origin_url: window.location.origin,
        case_id: caseId,
      });
      window.location.href = data.url;
    } catch (e) {
      const detail =
        e?.response?.data?.detail || "Couldn't start checkout — please try again.";
      toast.error(typeof detail === "string" ? detail : "Checkout failed.");
      setPayingCaseId(null);
    }
  };

  const filtered = useMemo(() => {
    if (filter === "all") return cases;
    return cases.filter((c) => c.status === filter);
  }, [cases, filter]);

  const [portalLoading, setPortalLoading] = useState(false);

  const openBillingPortal = async () => {
    setPortalLoading(true);
    try {
      const { data } = await api.post("/payments/portal", {
        return_url: `${window.location.origin}/dashboard`,
      });
      window.location.href = data.url;
    } catch (e) {
      const msg =
        e?.response?.status === 404
          ? e.response.data?.detail ||
            "No active subscription yet. Start one from the pricing section on the home page."
          : e?.response?.data?.detail || "Could not open billing portal.";
      toast.error(msg);
      setPortalLoading(false);
    }
  };

  const doLogout = () => {
    logout();
    toast.success("Signed out.");
    // Hard redirect avoids a React-router race where ProtectedRoute's
    // pending render bounces us to /login after user=null commits.
    window.location.assign("/");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gh-navy text-white grid place-items-center">
        <div className="flex items-center gap-3 text-white/60 text-sm">
          <Loader2 className="w-4 h-4 animate-spin text-[#10B981]" />
          Loading your dashboard…
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gh-navy text-white font-body">
      {/* Nav */}
      <header className="border-b border-white/5 sticky top-0 z-40 bg-[#0A0F1C]/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-md bg-gradient-to-br from-[#10B981] to-[#0e8f66] grid place-items-center shadow-lg shadow-emerald-500/30">
              <ShieldCheck className="w-4 h-4 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-display text-lg font-bold tracking-tight">
              Go <span className="text-[#D4AF37]">Higher</span>
              <span className="ml-2 text-xs font-body font-normal text-white/40">Dashboard</span>
            </span>
          </Link>
          <div className="flex items-center gap-3">
            <div className="hidden sm:flex items-center gap-2 text-xs text-white/50">
              <span className="w-7 h-7 rounded-full bg-gradient-to-br from-[#10B981] to-[#0e8f66] grid place-items-center font-display font-bold text-white text-[11px]">
                {(user?.name || "?").slice(0, 2).toUpperCase()}
              </span>
              <span className="text-white/70 font-semibold">{user?.name}</span>
            </div>
            {user?.role === "admin" && (
              <Link
                to="/admin"
                data-testid="dashboard-admin-link"
                className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10 hover:bg-[#D4AF37]/20 text-[#D4AF37] text-xs font-semibold transition-colors"
              >
                Admin
              </Link>
            )}
            <button
              onClick={doLogout}
              data-testid="dashboard-logout"
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-white/10 hover:border-white/25 text-white/70 hover:text-white text-xs font-semibold transition-colors"
            >
              <LogOut className="w-3.5 h-3.5" />
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10 lg:py-14">
        {/* Greeting + top stats */}
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#10B981] font-semibold">
              Client dashboard
            </div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold mt-2 leading-tight">
              Hey {(user?.name || "").split(" ")[0] || "there"} 👋
            </h1>
            <p className="mt-2 text-white/55 text-sm max-w-lg">
              Here's the live status of every removal case we're working on for you. You only pay
              for reviews Google actually removes.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <a
              href="/#book-call"
              className="inline-flex items-center gap-2 h-10 px-4 rounded-full border border-white/15 hover:border-white/30 text-white text-xs font-semibold transition-colors"
            >
              <CalendarClock className="w-3.5 h-3.5" />
              Book a call with your specialist
            </a>
            <button
              type="button"
              onClick={openBillingPortal}
              disabled={portalLoading}
              data-testid="dashboard-manage-subscription"
              className="inline-flex items-center gap-2 h-10 px-4 rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10 hover:bg-[#D4AF37]/20 text-[#D4AF37] text-xs font-semibold transition-colors disabled:opacity-60"
            >
              {portalLoading ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <CreditCard className="w-3.5 h-3.5" />
              )}
              Manage subscription
            </button>
            <a
              href="mailto:info@igohigher.com"
              className="inline-flex items-center gap-2 h-10 px-4 rounded-full bg-[#10B981] hover:bg-[#0ea774] text-white text-xs font-semibold shadow-lg shadow-emerald-500/25 transition-colors"
            >
              <Sparkles className="w-3.5 h-3.5" />
              Report a new review
            </a>
          </div>
        </div>

        {/* Onboarding empty state: no audit yet */}
        {!summary?.audit_request && cases.length === 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mt-8 relative rounded-3xl border border-[#D4AF37]/30 bg-gradient-to-br from-[#D4AF37]/[0.08] via-white/[0.02] to-[#10B981]/[0.04] overflow-hidden"
            data-testid="dashboard-onboarding"
          >
            <div
              aria-hidden
              className="absolute inset-0 opacity-40 pointer-events-none"
              style={{
                background:
                  "radial-gradient(500px 300px at 85% 15%, rgba(212,175,55,0.15), transparent 60%), radial-gradient(400px 250px at 15% 85%, rgba(16,185,129,0.12), transparent 60%)",
              }}
            />
            <div className="relative p-8 lg:p-10 grid lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-8">
                <div className="inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10 px-3 py-1 text-[10px] uppercase tracking-widest font-bold text-[#D4AF37]">
                  <Sparkles className="w-3 h-3" />
                  Step 1 of 3 · Get started
                </div>
                <h2 className="font-display text-2xl sm:text-3xl font-bold mt-4 leading-tight">
                  Welcome. Let's find the reviews we can remove.
                </h2>
                <p className="mt-3 text-white/60 text-sm leading-relaxed max-w-xl">
                  Submit your free audit and a reputation specialist will analyze your Google Business
                  Profile within one business day. You'll see every removable review listed here — with
                  a real-time status as we file, escalate, and confirm each removal.
                </p>

                <ol className="mt-6 grid sm:grid-cols-3 gap-3">
                  {[
                    { n: "01", t: "Submit your free audit", d: "Business name, GBP link, quick note." },
                    { n: "02", t: "We identify violators", d: "Policy-by-policy review, within 1 business day." },
                    { n: "03", t: "Track removals here", d: "Live case timeline as each one lands." },
                  ].map((s) => (
                    <li
                      key={s.n}
                      className="rounded-xl border border-white/10 bg-white/[0.02] px-4 py-3"
                    >
                      <div className="font-display text-[#D4AF37] text-xl font-bold tabular-nums">
                        {s.n}
                      </div>
                      <div className="mt-1 text-sm font-semibold text-white">{s.t}</div>
                      <div className="mt-0.5 text-[11px] text-white/50 leading-snug">{s.d}</div>
                    </li>
                  ))}
                </ol>

                <div className="mt-7 flex flex-wrap gap-3">
                  <a
                    href="/#audit"
                    data-testid="onboarding-cta-audit"
                    className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-[#10B981] hover:bg-[#0ea774] text-white text-sm font-semibold shadow-lg shadow-emerald-500/30 transition-colors"
                  >
                    <FileWarning className="w-4 h-4" />
                    Submit my free audit
                  </a>
                  <a
                    href="/#book-call"
                    className="inline-flex items-center gap-2 h-11 px-5 rounded-full border border-white/15 hover:border-white/30 text-white text-sm font-semibold transition-colors"
                  >
                    <CalendarClock className="w-4 h-4" />
                    Book a free 30-min call first
                  </a>
                </div>

                <div className="mt-5 inline-flex items-center gap-2 text-[11px] text-white/45">
                  <ShieldCheck className="w-3.5 h-3.5 text-[#10B981]" />
                  No removal, no charge — you only pay $195 per successfully removed review.
                </div>
              </div>

              {/* Right column — illustrative "coming soon" cases skeleton */}
              <div className="lg:col-span-4">
                <div className="relative rounded-2xl border border-white/10 bg-[#0d1424]/60 p-5">
                  <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-white/40">
                    Your removal timeline
                  </div>
                  <div className="mt-3 space-y-3">
                    {[
                      { label: "Submitted", filled: false },
                      { label: "Filed with Google", filled: false },
                      { label: "Escalated (if needed)", filled: false },
                      { label: "Removed", filled: false },
                    ].map((s, i) => (
                      <div key={s.label} className="flex items-center gap-3">
                        <span className="w-6 h-6 grid place-items-center rounded-full border border-white/15 text-[10px] font-bold tabular-nums text-white/40">
                          {i + 1}
                        </span>
                        <span className="text-sm text-white/50">{s.label}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-5 rounded-lg border border-dashed border-white/10 bg-white/[0.015] px-3 py-3 text-[11px] text-white/45 leading-relaxed">
                    Once your audit is complete, cases will appear here — each with a live
                    filing/escalation/removal timeline.
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* KPI cards — hidden until they have data */}
        {(summary?.audit_request || cases.length > 0) && (
          <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-3">
          <KpiCard
            icon={<BadgeCheck className="w-4 h-4" />}
            label="Removed"
            value={summary?.cases_by_status?.removed || 0}
            accent="emerald"
            testId="kpi-removed"
          />
          <KpiCard
            icon={<ShieldAlert className="w-4 h-4" />}
            label="In progress"
            value={
              (summary?.cases_by_status?.filed || 0) +
              (summary?.cases_by_status?.escalated || 0) +
              (summary?.cases_by_status?.submitted || 0) +
              (summary?.cases_by_status?.under_review || 0)
            }
            accent="gold"
            testId="kpi-in-progress"
          />
          <KpiCard
            icon={<Ban className="w-4 h-4" />}
            label="Not eligible"
            value={summary?.cases_by_status?.not_eligible || 0}
            hint="No charge — Google policy didn't match."
            accent="muted"
            testId="kpi-not-eligible"
          />
          <KpiCard
            icon={<Wallet className="w-4 h-4" />}
            label="Amount owed"
            value={`$${summary?.amount_owed_usd?.toLocaleString() || 0}`}
            hint={`${summary?.removed_count || 0} removed × $195 (pay after removal)`}
            accent="emerald"
            testId="kpi-amount"
          />
        </div>
        )}

        {/* Audit request card */}
        {summary?.audit_request && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
            className="mt-6 rounded-2xl border border-white/10 bg-gradient-to-r from-[#10B981]/[0.04] via-white/[0.02] to-transparent p-6 flex flex-wrap items-center justify-between gap-4 overflow-hidden"
          >
            <div className="flex items-center gap-4 min-w-0 flex-1">
              <div className="w-11 h-11 shrink-0 grid place-items-center rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#D4AF37]">
                <FileWarning className="w-5 h-5" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-[0.16em] text-white/40 font-bold">
                  Audit request · {summary.audit_request.status}
                </div>
                <div className="font-display font-bold text-white text-lg mt-0.5 truncate">
                  {summary.audit_request.business_name}
                </div>
                <div className="text-white/50 text-xs mt-0.5 truncate">
                  {summary.audit_request.problem_description}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3 text-xs text-white/50">
              <span>Submitted {timeAgo(summary.audit_request.created_at)}</span>
              {summary.audit_request.website_or_profile && (
                <a
                  href={
                    summary.audit_request.website_or_profile.startsWith("http")
                      ? summary.audit_request.website_or_profile
                      : `https://${summary.audit_request.website_or_profile}`
                  }
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-[#10B981] hover:text-[#0ea774]"
                >
                  Visit profile
                  <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
          </motion.div>
        )}

        {/* Progress dashboard — live pipeline + rating projection + activity feed */}
        <ProgressDashboard cases={cases} summary={summary} />

        {/* Filter bar */}
        <div className="mt-10 flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h2 className="font-display text-2xl font-bold">Your review cases</h2>
            <p className="text-white/50 text-sm mt-1">
              {cases.length} total · sorted newest first
            </p>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="dashboard-filters">
            {[
              { k: "all", label: "All" },
              { k: "filed", label: "Filed" },
              { k: "escalated", label: "Escalated" },
              { k: "removed", label: "Removed" },
              { k: "not_eligible", label: "Not eligible" },
            ].map((f) => (
              <button
                key={f.k}
                onClick={() => setFilter(f.k)}
                data-testid={`filter-${f.k}`}
                className={`h-8 px-3 rounded-full text-xs font-semibold border transition-colors ${
                  filter === f.k
                    ? "border-[#10B981]/40 bg-[#10B981]/[0.10] text-[#10B981]"
                    : "border-white/10 bg-white/[0.02] text-white/60 hover:text-white hover:border-white/25"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* Cases list */}
        <div id="cases-list" className="mt-6 space-y-3" data-testid="cases-list">
          {filtered.length === 0 ? (
            <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-10 text-center text-white/50 text-sm">
              No cases {filter !== "all" ? `with status "${filter.replace("_", " ")}"` : "yet"}.
            </div>
          ) : (
            filtered.map((c, i) => (
              <CaseRow
                key={c.id}
                c={c}
                idx={i}
                onPay={payInvoice}
                paying={payingCaseId === c.id}
              />
            ))
          )}
        </div>

        {/* Footer help */}
        <div className="mt-14 rounded-2xl border border-white/10 bg-white/[0.02] p-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-11 h-11 grid place-items-center rounded-xl bg-[#10B981]/10 border border-[#10B981]/30 text-[#10B981]">
              <ShieldCheck className="w-5 h-5" />
            </div>
            <div className="text-sm">
              <div className="font-display font-bold text-white">
                Something looks off?
              </div>
              <div className="text-white/50 text-xs mt-0.5">
                Your specialist replies within one business day.
              </div>
            </div>
          </div>
          <a
            href="mailto:info@igohigher.com"
            className="inline-flex items-center gap-2 h-10 px-4 rounded-full border border-white/15 hover:border-white/30 text-white text-xs font-semibold transition-colors"
          >
            Email info@igohigher.com
            <Copy className="w-3.5 h-3.5" />
          </a>
        </div>
      </main>
    </div>
  );
}

function KpiCard({ icon, label, value, hint, accent, testId }) {
  const map = {
    emerald: "border-[#10B981]/25 bg-[#10B981]/[0.05] text-[#10B981]",
    gold: "border-[#D4AF37]/25 bg-[#D4AF37]/[0.05] text-[#D4AF37]",
    muted: "border-white/10 bg-white/[0.02] text-white/60",
  };
  return (
    <div className={`rounded-2xl border p-5 ${map[accent]}`} data-testid={testId}>
      <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold">
        {icon}
        {label}
      </div>
      <div className="mt-3 font-display text-3xl font-bold text-white tabular-nums leading-none">
        {value}
      </div>
      {hint && <div className="mt-2 text-[11px] text-white/50 leading-tight">{hint}</div>}
    </div>
  );
}

function CaseRow({ c, idx, onPay, paying }) {
  const [open, setOpen] = useState(idx === 0);
  const meta = STATUS_META[c.status] || STATUS_META.submitted;
  const Icon = meta.icon;
  const stage = stageIndex(c.status);
  const isPaid = c.paid_status === "paid";

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: idx * 0.04 }}
      className={`rounded-2xl border ${meta.border} ${meta.bg} overflow-hidden`}
      data-testid={`case-${c.id}`}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex flex-wrap items-center justify-between gap-4 p-5 text-left hover:bg-white/[0.02] transition-colors"
      >
        <div className="flex items-center gap-4 min-w-0 flex-1">
          <div
            className="w-10 h-10 shrink-0 grid place-items-center rounded-xl border"
            style={{ borderColor: `${meta.color}55`, color: meta.color, background: `${meta.color}12` }}
          >
            <Icon className="w-4 h-4" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span
                className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full"
                style={{
                  color: meta.color,
                  background: `${meta.color}12`,
                  border: `1px solid ${meta.color}40`,
                }}
              >
                {meta.label}
              </span>
              <span className="text-xs text-white/45">{c.reason}</span>
            </div>
            <div className="mt-1 text-white/80 text-sm truncate italic">
              "{c.review_snippet}"
            </div>
          </div>
        </div>
        <div className="flex items-center gap-4 shrink-0">
          <div className="text-right hidden sm:block">
            <div className="font-display text-lg font-bold tabular-nums text-white">
              ${c.status === "removed" ? c.price_usd : 0}
              <span className="text-white/40 text-xs font-normal">
                {c.status === "removed" ? " · owed" : c.status === "not_eligible" ? " · no charge" : " · pending"}
              </span>
            </div>
            <div className="text-[11px] text-white/40 mt-0.5">
              {c.status === "removed" && c.resolved_at
                ? `Removed ${timeAgo(c.resolved_at)}`
                : `Opened ${timeAgo(c.created_at)}`}
            </div>
          </div>
          <div className="w-7 h-7 grid place-items-center rounded-full border border-white/10 text-white/50">
            {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </div>
        </div>
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="border-t border-white/5"
          >
            <div className="p-5 grid md:grid-cols-12 gap-6">
              {/* Progress stepper */}
              <div className="md:col-span-5">
                <div className="text-[10px] uppercase tracking-widest text-white/40 font-bold mb-3">
                  Progress
                </div>
                <ol className="space-y-2.5">
                  {[
                    { key: "submitted", label: "Submitted" },
                    { key: "filed", label: "Filed with Google" },
                    { key: "escalated", label: "Escalated (if needed)" },
                    { key: "removed", label: "Removed" },
                  ].map((step, i) => {
                    const active = stage >= i && c.status !== "not_eligible";
                    const isCurrent = STAGE_ORDER.indexOf(c.status) === i;
                    return (
                      <li key={step.key} className="flex items-center gap-3">
                        <span
                          className={`w-6 h-6 grid place-items-center rounded-full border text-[10px] font-bold tabular-nums ${
                            active
                              ? "bg-[#10B981] border-[#10B981] text-white"
                              : "border-white/15 text-white/40"
                          }`}
                        >
                          {active ? "✓" : i + 1}
                        </span>
                        <span
                          className={`text-sm ${
                            active ? "text-white" : "text-white/45"
                          } ${isCurrent ? "font-semibold" : ""}`}
                        >
                          {step.label}
                          {isCurrent && (
                            <span className="ml-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-[#D4AF37] font-bold">
                              <span className="w-1 h-1 rounded-full bg-[#D4AF37] animate-pulse" />
                              current
                            </span>
                          )}
                        </span>
                      </li>
                    );
                  })}
                  {c.status === "not_eligible" && (
                    <li className="mt-2 rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2 text-[11px] text-white/55">
                      This review didn't clearly violate a Google policy — so no removal was
                      attempted and there is no charge.
                    </li>
                  )}
                </ol>
              </div>

              {/* Timeline */}
              <div className="md:col-span-7">
                <div className="text-[10px] uppercase tracking-widest text-white/40 font-bold mb-3">
                  Timeline
                </div>
                <ol className="relative border-l border-white/10 ml-2 space-y-4 pl-4">
                  {(c.timeline || []).map((t, i) => (
                    <li key={i} className="relative">
                      <span
                        className="absolute -left-[22px] top-1.5 w-2 h-2 rounded-full"
                        style={{ background: i === 0 ? meta.color : "#94A3B8" }}
                      />
                      <div className="text-xs text-white/40 tabular-nums">
                        {t.at ? new Date(t.at).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric" }) : ""}
                      </div>
                      <div className="text-sm text-white font-semibold">{t.label}</div>
                      {t.detail && <div className="text-xs text-white/55 mt-0.5">{t.detail}</div>}
                    </li>
                  ))}
                </ol>

                {c.status === "removed" && (
                  <div className="mt-5 rounded-xl border border-[#10B981]/30 bg-[#10B981]/[0.06] p-4 flex items-center justify-between gap-3">
                    <div className="text-sm">
                      <div className="font-display font-bold text-white">
                        {isPaid ? "Invoice paid — thank you." : "Invoice ready — $195"}
                      </div>
                      <div className="text-white/55 text-xs mt-0.5">
                        {isPaid
                          ? `Received ${timeAgo(c.paid_at)} · card or ACH.`
                          : "Pay securely via Stripe. Card or ACH bank transfer."}
                      </div>
                    </div>
                    {isPaid ? (
                      <span className="inline-flex items-center gap-1.5 rounded-full border border-[#10B981]/30 bg-[#10B981]/[0.10] px-3 py-1.5 text-[10px] uppercase tracking-widest font-bold text-[#10B981]">
                        <BadgeCheck className="w-3.5 h-3.5" />
                        Paid
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => onPay?.(c.id)}
                        disabled={paying}
                        data-testid={`pay-case-${c.id}`}
                        className="inline-flex items-center gap-2 h-10 px-4 rounded-full bg-[#10B981] hover:bg-[#0ea774] text-white text-xs font-semibold shadow-lg shadow-emerald-500/25 transition-colors disabled:opacity-60"
                      >
                        {paying ? (
                          <>
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            Starting…
                          </>
                        ) : (
                          <>
                            <Wallet className="w-3.5 h-3.5" />
                            Pay $195
                          </>
                        )}
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
